import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { formatPhone } from '../lib/formatPhone';
import { useEntityPermission } from '../lib/usePermissions';
import { latestRowAttr } from '../lib/inlineAttribution';
import { LastUpdated } from './LastUpdated';

/**
 * EntityContactsPanel — Domain 17.1 Contacts tab (list / add / edit) over the
 * real L2-crm routes. Drop it on any host-entity detail (client, assessor,
 * collector, jurisdiction, property, parcel) with the entity_type + id and it wires:
 *
 *   - LIST : GET  /api/contacts-by-entity/:entityType/:entityId  (enriched read —
 *            role + primary email + primary phone from the contact_list junction)
 *   - ADD  : POST /api/entity-contacts/:entityType/:entityId     (create + link in
 *            one transaction; supports first/last/company, title, type, role,
 *            primary email, primary phone)
 *   - EDIT : PUT  /api/entity-contacts/:entityType/:entityId/:contactId  (the same
 *            bespoke form as ADD — updates the contact + its primary email/phone
 *            in the side tables + its role on this link, all of which the generic
 *            contacts CRUD can't reach).
 *
 * Contacts render as a uniform CARD grid (name + type/role badges + contact
 * methods) — the single presentation every host shares, so a contact looks the
 * same on a client, a property, a parcel, or a government profile.
 *
 * Controls render only for holders of the 'contact' add/edit grants (the same
 * permission the server gate enforces — the UI never offers a button the
 * backend will 403).
 */

export type ContactHostEntity = 'clients' | 'assessors' | 'collectors' | 'jurisdictions' | 'properties' | 'parcels';

export interface EntityContact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  title: string | null;
  type: string | null;
  role: string | null;
  is_inherited?: number | boolean | null;
  email: string | null;
  phone_number: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface EntityContactsPanelProps {
  entityType: ContactHostEntity;
  entityId: string;
}

const DEFAULT_TYPE: Record<ContactHostEntity, string> = {
  clients: 'client_rep',
  assessors: 'assessor_rep',
  collectors: 'collector_rep',
  jurisdictions: 'other',
  properties: 'contact',
  parcels: 'contact',
};

const contactName = (c: EntityContact) =>
  [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || '—';

const isInherited = (c: EntityContact) => c.is_inherited === true || c.is_inherited === 1;

// Humanize a raw registry token (e.g. client_rep → "Client rep") for a Select
// label without losing the stored value.
const humanizeToken = (t: string) => {
  const s = t.replace(/_/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// GET /api/org-settings/{contact-types,contact-roles} may return a bare array or
// a { values: [...] } envelope depending on the setting shape.
function normalizeValues(data: string[] | { values: string[] } | undefined): string[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.values || [];
}

export function EntityContactsPanel({ entityType, entityId }: EntityContactsPanelProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [editContact, setEditContact] = useState<EntityContact | null>(null);

  const canAdd = useEntityPermission('contact', 'can_add');
  const canEdit = useEntityPermission('contact', 'can_edit');

  // §5.5 strict visibility — a Property/Parcel profile shows ONLY its own
  // directly-attached contacts, never contacts that reached it by parcel→
  // property→client inheritance. (The client profile uses ClientContactsGrouped,
  // which reads each level directly, so this panel's client host is unaffected.)
  const direct = entityType === 'properties' || entityType === 'parcels';
  const queryKey = ['contacts-by-entity', entityType, entityId, direct ? 'direct' : 'all'];
  const contactsQuery = useQuery<EntityContact[]>({
    queryKey,
    queryFn: ({ signal }) => api.get<EntityContact[]>(`/api/contacts-by-entity/${entityType}/${entityId}${direct ? '?direct=1' : ''}`, signal),
    enabled: !!entityId,
  });

  const contacts = contactsQuery.data ?? [];
  // A5 — section attribution: the most-recent contact edit in this section,
  // "Last updated by {name} · {date}", same treatment as the overview boxes.
  const attr = latestRowAttr(contacts);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
            {contactsQuery.isLoading ? 'Loading contacts…' : `${contacts.length} contact${contacts.length === 1 ? '' : 's'} on file`}
          </span>
          {attr && <LastUpdated updatedBy={attr.by} updatedAt={attr.at} />}
        </div>
        {canAdd && (
          <Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>+ Add contact</Button>
        )}
      </div>

      {contactsQuery.error && (
        <div style={{
          padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
        }}>{(contactsQuery.error as Error).message}</div>
      )}

      {!contactsQuery.isLoading && !contactsQuery.error && contacts.length === 0 && (
        <div style={{
          padding: 'var(--sp-6)', background: 'var(--color-card)', border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center', fontSize: 'var(--fs-md)',
        }}>
          No contacts on this record yet.{canAdd ? ' Use “+ Add contact” to link one.' : ''}
        </div>
      )}

      {contacts.length > 0 && (
        <div
          data-testid="contacts-card-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
            gap: 'var(--sp-4)',
          }}
        >
          {contacts.map((c) => (
            <ContactCard
              key={`${c.id}-${c.role ?? ''}`}
              contact={c}
              canEdit={canEdit}
              onEdit={() => setEditContact(c)}
            />
          ))}
        </div>
      )}

      <AddContactModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        entityType={entityType}
        entityId={entityId}
        invalidateKey={queryKey}
      />

      {editContact && (
        <AddContactModal
          open={!!editContact}
          editContact={editContact}
          onClose={() => setEditContact(null)}
          entityType={entityType}
          entityId={entityId}
          invalidateKey={queryKey}
        />
      )}
    </div>
  );
}

/**
 * ContactCard — the uniform card treatment for a single linked contact. Name +
 * inherited marker, Type + Role badges, then the contact methods (email as a
 * mailto link, phone). This replaces the former bare table so every host renders
 * contacts identically.
 */
export function ContactCard({
  contact, canEdit, onEdit, coverage,
}: {
  contact: EntityContact;
  canEdit: boolean;
  onEdit: () => void;
  /** Optional "covers" line (the property/parcel a client-profile card relates to). */
  coverage?: React.ReactNode;
}) {
  const inherited = isInherited(contact);
  const type = contact.type;
  const role = contact.role;
  return (
    <div
      data-testid="contact-card"
      style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)',
        padding: 'var(--sp-4)', background: 'var(--color-card)',
        border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 0 }}>
          <span style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>
            {contactName(contact)}
          </span>
          {contact.title && (
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{contact.title}</span>
          )}
        </div>
        {inherited && <Badge variant="info">Inherited</Badge>}
      </div>

      {(type || role) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
          {type && <Badge variant="neutral">{type.replace(/_/g, ' ')}</Badge>}
          {role && <Badge variant="info">{role.replace(/_/g, ' ')}</Badge>}
        </div>
      )}

      {coverage && (
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{coverage}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', fontSize: 'var(--fs-sm)' }}>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'baseline' }}>
          <span style={{ color: 'var(--color-ink-3)', minWidth: 52 }}>Email</span>
          {contact.email
            ? <a href={`mailto:${contact.email}`} style={{ color: 'var(--brand-primary)', overflowWrap: 'anywhere' }}>{contact.email}</a>
            : <span style={{ color: 'var(--color-muted)' }}>—</span>}
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'baseline' }}>
          <span style={{ color: 'var(--color-ink-3)', minWidth: 52 }}>Phone</span>
          {contact.phone_number
            ? <a href={`tel:${contact.phone_number}`} style={{ color: 'var(--brand-primary)' }}>{contact.phone_number}</a>
            : <span style={{ color: 'var(--color-muted)' }}>—</span>}
        </div>
      </div>

      {canEdit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
          <Button variant="ghost" size="sm" onClick={onEdit}>Edit</Button>
        </div>
      )}
    </div>
  );
}

interface AddContactForm {
  first_name: string;
  last_name: string;
  company_name: string;
  title: string;
  type: string;
  role: string;
  email: string;
  phone: string;
}

export function AddContactModal({
  open, onClose, entityType, entityId, invalidateKey, editContact, initialEmail,
}: {
  open: boolean;
  onClose: () => void;
  entityType: ContactHostEntity;
  entityId: string;
  invalidateKey: (string | undefined)[];
  editContact?: EntityContact | null;
  /** Prefill for a new contact (the activity composer's "save as contact" offer). */
  initialEmail?: string;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEdit = !!editContact;
  const emptyForm: AddContactForm = {
    first_name: '', last_name: '', company_name: '', title: '',
    type: DEFAULT_TYPE[entityType], role: '', email: initialEmail || '', phone: '',
  };
  const [form, setForm] = useState<AddContactForm>(() => editContact ? {
    first_name: editContact.first_name || '',
    last_name: editContact.last_name || '',
    company_name: editContact.company_name || '',
    title: editContact.title || '',
    type: editContact.type || DEFAULT_TYPE[entityType],
    role: editContact.role || '',
    email: editContact.email || '',
    phone: editContact.phone_number || '',
  } : emptyForm);
  const [error, setError] = useState<string | null>(null);

  // Both Type and Role are org-configurable registries fed by org-settings.
  // Fetched when the form opens; cached (shared query keys with Org Settings).
  const typesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'contact-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/contact-types', signal),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const rolesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'contact-roles'],
    queryFn: ({ signal }) => api.get('/api/org-settings/contact-roles', signal),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Keep the current type value selectable even if the org removed it from the
  // registry (mirrors the timezone-editor merge pattern).
  const configuredTypes = normalizeValues(typesQuery.data);
  const typeValues = !form.type || configuredTypes.includes(form.type)
    ? configuredTypes
    : [form.type, ...configuredTypes];
  const typeOptions = typeValues.map((t) => ({ value: t, label: humanizeToken(t) }));

  // Role registry, plus the implicit "primary" (empty value → server defaults to
  // 'primary'), so submitting with no explicit role preserves prior behavior.
  const roleOptions = [
    { value: '', label: 'Primary (default)' },
    ...normalizeValues(rolesQuery.data).map((r) => ({ value: r, label: humanizeToken(r) })),
  ];

  const set = (k: keyof AddContactForm, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation<EntityContact, Error, AddContactForm>({
    mutationFn: (values) => {
      const payload: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) {
        // On edit, send every field (a cleared '' clears email/phone/title); on
        // add, omit empties so the server defaults apply.
        if (isEdit || v.trim() !== '') payload[k] = v.trim();
      }
      return isEdit
        ? api.put<EntityContact>(`/api/entity-contacts/${entityType}/${entityId}/${editContact!.id}`, payload)
        : api.post<EntityContact>(`/api/entity-contacts/${entityType}/${entityId}`, payload);
    },
    onSuccess: () => {
      toast({ variant: 'success', message: isEdit ? 'Contact updated.' : 'Contact added.' });
      queryClient.invalidateQueries({ queryKey: invalidateKey });
      setForm(emptyForm);
      setError(null);
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? (err.body as { error: string }).error
        : err.message;
      setError(msg);
      toast({ variant: 'error', message: msg });
    },
  });

  const canSubmit = form.first_name.trim() || form.last_name.trim() || form.company_name.trim();

  const handleSubmit = () => {
    setError(null);
    if (!canSubmit) {
      setError('A contact needs at least a first name, last name, or company name.');
      return;
    }
    mutation.mutate(form);
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) { setForm(emptyForm); setError(null); onClose(); } }}
      title={isEdit ? 'Edit contact' : 'Add contact'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => { setForm(emptyForm); setError(null); onClose(); }} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!canSubmit} onClick={handleSubmit}>
            {isEdit ? 'Save' : 'Add contact'}
          </Button>
        </>
      }
    >
      {error && (
        <div style={{
          padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-4)',
          background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)',
        }}>{error}</div>
      )}
      <form
        onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 'var(--sp-4)' }}
      >
        <Field label="First name"><Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} autoFocus /></Field>
        <Field label="Last name"><Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} /></Field>
        <Field label="Company"><Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} /></Field>
        <Field label="Title"><Input value={form.title} onChange={(e) => set('title', e.target.value)} /></Field>
        <Field label="Type" hint="Configured in Org Settings → Contact Types">
          <Select
            options={typeOptions}
            value={form.type}
            onChange={(e) => set('type', e.target.value)}
          />
        </Field>
        <Field label="Role" hint="Configured in Org Settings → Contact Roles">
          <Select
            options={roleOptions}
            value={form.role}
            onChange={(e) => set('role', e.target.value)}
          />
        </Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field label="Phone"><Input value={form.phone} onChange={(e) => set('phone', formatPhone(e.target.value))} inputMode="tel" placeholder="XXX-XXX-XXXX" /></Field>
      </form>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-2)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{hint}</span>}
    </label>
  );
}
